import React, { useEffect, useState } from "react";
import { Box, Button,Snackbar, useTheme, IconButton, Stack} from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import { IDocumentMap, IDocumentMappingConfiguration } from "../../../../models/configuration/bundles/IDocumentMappings";
import AddDocumentMapping from "../AddDocumentMapping";
import { useDocumentMappings } from "../../../../hooks/useDocumentMappings";
import DocumentLookup from "./DocumentLookup";
import EncompassService from "../../../../services/encompassService";
import {  documentMappingColumnsRef } from "../configbundlingdata";
import { useActions } from "../../../../hooks/useActions";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state/store";
import { AlertSeverity } from "../../../../constants/AlertTypes";
import ConfigLoad from "../../../../components/shared/config/ConfigLoad";
import { DtDialog } from "../../../../components/ui/dialog";
import DtAlert from "../../../../components/ui/alert/DtAlert";
import { DtDataGrid } from "../../../../components/ui/dataGrid";
import { Search } from "@mui/icons-material";

interface DocumentMappingDialogProps {
  open: boolean;
  closeFxtn: () => void;
}

const DocumentMappingDialog: React.FC<DocumentMappingDialogProps> = ({ open, closeFxtn }) => {

  const theme = useTheme();
  const { saveDocumentMappings } = useActions();
  const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
  const [documentMaps, setDocumentMaps] = useState<Array<IDocumentMap> | undefined>([]);
  const [addDocVisible, setAddDocVisible] = useState<boolean>(false);
  const [docLookupOpen, setDocLookupOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<IDocumentMap>();
  const [addCurrentItem, setAddCurrentItem] = useState(false);
  const [addedEncompassDocName, setAddedEncompassDocName] = useState("");
  const [currPage, setCurrPage] = useState(1);

  // Hooks
 const {data:documentMappings,loading}= useDocumentMappings();

  // State Confirmations
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');

  useEffect(() => {
    setDocumentMaps(documentMappings?.Configuration.DocumentMaps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDocumentMaps(documentMappings?.Configuration.DocumentMaps);
  }, [documentMappings]);  

  const addItem = (docIndex: number, docName: string, encompassDocName: string) => {
    var map: IDocumentMap = {
      DocumentIndex: docIndex.toString(),
      ConformXDocumentName: docName,
      EncompassDocumentName: encompassDocName,
      isnew: true,
    };

    if (documentMaps != null) {
      setDocumentMaps([map, ...documentMaps!]);
    }

    setAddDocVisible(false);
    setAddedEncompassDocName("");
    setCurrPage(1);
  };

  const deleteItem = (e: any, item: IDocumentMap) => {
    if (documentMaps == null) return;

    var docindex = documentMaps!.indexOf(item);
    if (docindex > -1) {
      setDocumentMaps([
        ...documentMaps
          .slice(0, docindex)
          .concat(documentMaps.slice(docindex + 1)),
      ]);
    }
  };

  const getItemForeColor = (item: IDocumentMap) => {
    if (item.isnew) {
      return theme.palette.success.main;
    }
  };

  const showItemLookup = (e: any, item: IDocumentMap) => {
    setCurrentItem(item);
    setDocLookupOpen(true);
  };

  const setItemLookupUpdated = (item: string) => {
    if (addCurrentItem) {
      setAddedEncompassDocName(item);
      setAddCurrentItem(false);
    } else {
      if (currentItem != null && item) {
        setDocumentMaps(documentMaps!.map(map =>
          map.DocumentIndex === currentItem.DocumentIndex
            ? { ...map, EncompassDocumentName: item, ischanged: true }
            : map
        ));
      }
    }

    setDocLookupOpen(false);
  };

  const showAddSelectedItem = () => {
    setAddCurrentItem(true);
    setDocLookupOpen(true);
  };

  const saveChanges = async () => {
    try {
      if (documentMaps == null) return;
      const configId = EncompassService.getConfigId() as string;
      const documentMappingConfig: IDocumentMappingConfiguration = {
        Configuration: {
          DateSaved: formatDate(new Date()),
          DocumentMaps: documentMaps!,
        },
        configurationId: configId,
      };

      await saveDocumentMappings(configId, documentMappingConfig, accessToken);
      openAlert("Successfully Saved Changes", "success");
      closeFxtn();
    } catch (e) {
      const error = `${e}`;
      console.log(error)
      openAlert("Error Saving Changes", "error");
      console.log("Save Error: ", error);
    }
  }

 const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    const offset = date.getTimezoneOffset();
    const offsetSign = offset < 0 ? "+" : "-";
    const offsetHours = Math.abs(Math.floor(offset / 60))
      .toString()
      .padStart(2, "0");
    const offsetMinutes = Math.abs(offset % 60)
      .toString()
      .padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
  };

  if (documentMappings == null) {
    return <></>;
  }

  // Alert Handling 
  const handleSnackBarClose = (_event: any) => { setAlertOpen(false); }
  const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setAlertOpen(true);
  }

  return (
    <>
      <DocumentLookup
        documentList={
          Array.from(
            new Set(
              (documentMappings?.Configuration?.DocumentMaps ?? []).map(
                (x) => x.EncompassDocumentName
              )
            )
          )
        }
        open={docLookupOpen}
        closeFxtn={() => setDocLookupOpen(false)}
        itemSelected={setItemLookupUpdated}
      />
      <DtDialog
        key="docMappingDialog"
        title="Encompass to Docutech Document Configuration"
        open={open}
        onClose={closeFxtn}
        fullWidth
        maxWidth="lg"
        actions={<>
          {addDocVisible ? (
            <AddDocumentMapping
              open={true}
              addItemFunction={addItem}
              showSelectItem={showAddSelectedItem}
              encompassDocName={addedEncompassDocName}
              onClose={() => setAddDocVisible(currentState => !currentState)}
            />
          ) : (
            <Box className={["flexLeft", "height100"].join(" ")}>
              <Button onClick={() => setAddDocVisible(true)}>Add Mapping</Button>
            </Box>
          )}
          <Button variant="contained" size="small" onClick={saveChanges}>{"Save"}</Button>
        </>
      } sx={{content: {pt: 1}}}>
        {loading && (
          <ConfigLoad loading={loading} />
        )}
        
        <Stack direction="column" spacing={1} >
         <DtAlert severity="info"> New Indexes and Docutech Forms are added as Pushback Events are processed</DtAlert>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, height: "100%" }}
          >
             <DtDataGrid
              columns={documentMappingColumnsRef(getItemForeColor, showItemLookup)}
              data={documentMaps ?? []}
              tableName="documentMappings"
              containerSx={{minHeight: "55vh", maxHeight: "55vh"}}
              responsivePageSize={true}
              responsivePageConfig={{heightRatio: 0.7}}
              dense={true}
              paged={true}
              currentPage={currPage}
              onPageChange={setCurrPage}
              actionsConfig={{
                mode: 'inline',
                actions: [{
                  type: 'delete',
                  onClick(row, rowIndex) {
                    deleteItem(null, row);
                  },
                }]
              }}
            />
          </Box>
        </Box>
        </Stack>
      </DtDialog>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={alertOpen}
        autoHideDuration={4000}
        onClose={handleSnackBarClose}
      >
        <MuiAlert
          onClose={handleSnackBarClose}
          severity={alertSeverity}
          sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default DocumentMappingDialog;